import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

export type PinInputSize = 'sm' | 'md' | 'lg';
export type PinInputType = 'numeric' | 'alphanumeric';

export interface PinInputProps {
  /** Number of slots. Default 6 (OTP typical). */
  length?: number;
  /** Characters allowed per slot — 0-9 for `numeric`, [A-Za-z0-9] for
   *  `alphanumeric`. Invalid chars are silently ignored on input. */
  type?: PinInputType;
  /** Display as dots (for passwords) instead of the literal character. */
  mask?: boolean;
  /** Controlled value (must equal `length` characters). */
  value?: string;
  /** Uncontrolled default. */
  defaultValue?: string;
  /** Fires on every change. Emits the full current string — partially
   *  filled slots contribute their characters, empty slots contribute ''. */
  onValueChange?: (value: string) => void;
  /** Fires once the last slot is filled (i.e. value length equals `length`). */
  onComplete?: (value: string) => void;
  size?: PinInputSize;
  disabled?: boolean;
  invalid?: boolean;
  /** Label for assistive tech; every slot announces `${label} digit 3 of 6`. */
  label?: string;
  autoFocus?: boolean;
  className?: string;
}

const NUMERIC = /^[0-9]$/;
const ALPHA = /^[A-Za-z0-9]$/;

/** Segmented OTP / PIN input — one character per slot, paste across slots,
 *  arrow-key navigation, backspace retreats. Spec 15 §12. */
export function PinInput(props: PinInputProps): ReactNode {
  const {
    length = 6,
    type = 'numeric',
    mask = false,
    value: controlled,
    defaultValue = '',
    onValueChange,
    onComplete,
    size = 'md',
    disabled = false,
    invalid = false,
    label,
    autoFocus = false,
    className,
  } = props;

  const isControlled = controlled !== undefined;
  const [uncontrolled, setUncontrolled] = useState<string>(defaultValue.slice(0, length));
  const value = isControlled ? controlled.slice(0, length) : uncontrolled;
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const pattern = useMemo(() => (type === 'numeric' ? NUMERIC : ALPHA), [type]);

  const setValue = useCallback(
    (next: string): void => {
      const clipped = next.slice(0, length);
      if (!isControlled) setUncontrolled(clipped);
      onValueChange?.(clipped);
      if (clipped.length === length) onComplete?.(clipped);
    },
    [isControlled, length, onComplete, onValueChange],
  );

  const focusSlot = useCallback(
    (index: number): void => {
      const el = inputsRef.current[Math.max(0, Math.min(length - 1, index))];
      el?.focus();
      el?.select();
    },
    [length],
  );

  const handleChange = useCallback(
    (index: number, raw: string): void => {
      // Paste inside a single slot may deliver many chars — route them into
      // subsequent slots for the paste-to-fill UX.
      const chars = raw.split('').filter((c) => pattern.test(c));
      if (chars.length === 0) {
        const next = value.slice(0, index) + value.slice(index + 1);
        setValue(next);
        return;
      }
      const arr = value.split('');
      let cursor = index;
      for (const c of chars) {
        if (cursor >= length) break;
        arr[cursor] = c;
        cursor += 1;
      }
      setValue(arr.join(''));
      focusSlot(cursor);
    },
    [focusSlot, length, pattern, setValue, value],
  );

  const handleKey = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        focusSlot(index - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        focusSlot(index + 1);
      } else if (e.key === 'Backspace') {
        if ((value[index] ?? '') === '') {
          e.preventDefault();
          focusSlot(index - 1);
          const arr = value.split('');
          arr[index - 1] = '';
          setValue(arr.join(''));
        }
      }
    },
    [focusSlot, setValue, value],
  );

  const handlePaste = useCallback(
    (index: number, e: ClipboardEvent<HTMLInputElement>): void => {
      const text = e.clipboardData.getData('text');
      if (text === '') return;
      e.preventDefault();
      handleChange(index, text);
    },
    [handleChange],
  );

  const classes = ['vq-pin-input', className].filter(Boolean).join(' ');
  const slots: number[] = [];
  for (let i = 0; i < length; i++) slots.push(i);

  return (
    <div
      className={classes}
      data-component="PinInput"
      data-size={size}
      data-state={disabled ? 'disabled' : invalid ? 'invalid' : 'idle'}
      role="group"
      aria-label={label}
    >
      {slots.map((i) => (
        <input
          key={i}
          ref={(el) => {
            inputsRef.current[i] = el;
          }}
          className="vq-pin-input__slot"
          type={mask ? 'password' : 'text'}
          inputMode={type === 'numeric' ? 'numeric' : 'text'}
          pattern={type === 'numeric' ? '[0-9]*' : '[A-Za-z0-9]*'}
          maxLength={1}
          autoComplete="one-time-code"
          aria-label={`${label ?? 'PIN'} ${String(i + 1)} / ${String(length)}`}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          value={value[i] ?? ''}
          autoFocus={autoFocus && i === 0}
          onChange={(e) => {
            handleChange(i, e.target.value);
          }}
          onKeyDown={(e) => {
            handleKey(i, e);
          }}
          onPaste={(e) => {
            handlePaste(i, e);
          }}
          onFocus={(e) => {
            e.target.select();
          }}
        />
      ))}
    </div>
  );
}
